import type { Middleware } from '@floating-ui/react-dom';
import type { PartialDeep, Styles } from 'react-joyride';

/** Fallback when the navbar isn't mounted yet (SSR / first paint). */
export const TOUR_SCROLL_OFFSET_FALLBACK = 96;
/** Gap between navbar bottom and tour tooltip / scroll target. */
const TOUR_NAV_GAP_PX = 16;

/**
 * Live navbar clearance for tour scroll + Floating UI padding.
 * Measures `[data-tour-navbar]` so a taller/shorter nav doesn't misplace tooltips.
 */
export const getTourScrollOffset = (): number => {
  if (typeof document === 'undefined') {
    return TOUR_SCROLL_OFFSET_FALLBACK;
  }

  const nav = document.querySelector('[data-tour-navbar]');
  if (!(nav instanceof HTMLElement)) {
    return TOUR_SCROLL_OFFSET_FALLBACK;
  }

  const { height } = nav.getBoundingClientRect();
  if (!Number.isFinite(height) || height <= 0) {
    return TOUR_SCROLL_OFFSET_FALLBACK;
  }

  return Math.ceil(height) + TOUR_NAV_GAP_PX;
};

/** @deprecated Prefer getTourScrollOffset() — static fallback only. */
export const TOUR_SCROLL_OFFSET = TOUR_SCROLL_OFFSET_FALLBACK;

const VIEWPORT_EDGE_PADDING = 24;

/**
 * Floating UI can retain an off-screen x-coordinate while DevTools changes the
 * viewport width. This final middleware clamps every computed position to the
 * current viewport, including after live resize events.
 */
const clampToViewport: Middleware = {
  name: 'clampToViewport',
  fn({ x, y, rects, middlewareData }) {
    const maxX = Math.max(
      VIEWPORT_EDGE_PADDING,
      window.innerWidth - rects.floating.width - VIEWPORT_EDGE_PADDING,
    );
    const clampedX = Math.min(Math.max(x, VIEWPORT_EDGE_PADDING), maxX);
    const dx = clampedX - x;
    const arrow = middlewareData.arrow;

    return {
      x: clampedX,
      y,
      data:
        dx && arrow
          ? {
              ...middlewareData,
              arrow: { ...arrow, x: (arrow.x ?? 0) - dx },
            }
          : middlewareData,
    };
  },
};

const liveTopPadding = () => getTourScrollOffset();

export const tourFloatingOptions = {
  // Viewport-relative; autoUpdate keeps the tooltip glued to the target on scroll.
  strategy: 'fixed' as const,
  autoUpdate: {
    ancestorScroll: true,
    ancestorResize: true,
    elementResize: true,
    layoutShift: true,
    animationFrame: true,
  },
  flipOptions: {
    // Getters so padding tracks navbar height on resize / route changes.
    get padding() {
      return {
        top: liveTopPadding(),
        bottom: 24,
        left: 16,
        right: 16,
      };
    },
  },
  shiftOptions: {
    get padding() {
      return {
        top: liveTopPadding(),
        bottom: 24,
        left: 24,
        right: 24,
      };
    },
    // Allow vertical adjust so flip top↔bottom can settle cleanly on scroll.
    mainAxis: true,
    crossAxis: true,
    rootBoundary: 'viewport' as const,
  },
  // Mutable copy — FloatingOptions.middleware is not readonly.
  middleware: [clampToViewport],
};

export const joyrideStyles: PartialDeep<Styles> = {
  tooltip: {
    borderRadius: 12,
    padding: 0,
    border: '1px solid rgba(147, 51, 234, 0.45)',
    boxShadow:
      '0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(147, 51, 234, 0.12)',
    overflow: 'hidden',
  },
  tooltipContainer: {
    textAlign: 'left',
    padding: '22px 24px 8px',
  },
  tooltipTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    marginBottom: 10,
    color: '#f9fafb',
    lineHeight: 1.35,
  },
  tooltipContent: {
    fontSize: '0.9375rem',
    lineHeight: 1.65,
    padding: '6px 0 4px',
    color: '#d1d5db',
  },
  tooltipFooter: {
    padding: '14px 24px 20px',
    marginTop: 4,
    borderTop: '1px solid rgba(147, 51, 234, 0.22)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tooltipFooterSpacer: {
    flex: 1,
  },
  buttonPrimary: {
    backgroundColor: 'var(--brand-primary, #9333ea)',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 600,
    padding: '9px 20px',
    border: 'none',
    cursor: 'pointer',
    color: '#ffffff',
    boxShadow: '0 2px 10px rgba(147, 51, 234, 0.4)',
  },
  buttonBack: {
    color: '#9ca3af',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '9px 16px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
  },
  buttonSkip: {
    color: '#9ca3af',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '9px 14px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
  },
  buttonClose: {
    color: '#9ca3af',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
  },
  beacon: {
    zIndex: 10001,
  },
  beaconInner: {
    backgroundColor: '#9333ea',
  },
  beaconOuter: {
    border: '2px solid #9333ea',
    backgroundColor: 'rgba(147, 51, 234, 0.15)',
  },
  floater: {
    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))',
  },
};

export const joyrideOptions = {
  primaryColor: '#9333ea',
  backgroundColor: '#111827',
  textColor: '#f3f4f6',
  overlayColor: 'rgba(0, 0, 0, 0.7)',
  arrowColor: '#111827',
  zIndex: 10000,
  showProgress: true,
  scrollDuration: 300,
  spotlightPadding: 8,
  spotlightRadius: 8,
  offset: 10,
  width: 'min(400px, calc(100vw - 3rem))',
  blockTargetInteraction: true,
  skipScroll: true,
  // Minimize to purple beacon on blank-area click (do not skip/advance step)
  overlayClickAction: 'replay' as const,
  dismissKeyAction: 'replay' as const,
} as const;
